import * as React from "react";
import { Camera, Loader2, Trash2, Plus, ImageIcon } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { uploadPhoto } from "@/lib/uploadPhoto.js";
import { usePlan } from "@/contexts/PlanContext.js";
import { cn } from "@/lib/utils.js";

export interface ItemPhoto {
  id: string;
  displayUrl: string | null;
  thumbUrl: string | null;
  hasOriginal: boolean;
  sortOrder: number;
  createdAt: string;
}

interface PhotoGalleryProps {
  itemId: string;
  /** Legacy single photoUrl from the item (for backward compat during migration). */
  legacyPhotoUrl?: string | null;
  editable?: boolean;
  /** Called when the gallery changes (photos added/removed), so parent can track dirty state. */
  onChanged?: () => void;
}

/**
 * Multi-photo gallery for an item on web. Shows a horizontal row of thumbnails
 * with the ability to add more photos (up to plan limit) and delete existing ones.
 */
export function PhotoGallery({ itemId, legacyPhotoUrl, editable = true, onChanged }: PhotoGalleryProps) {
  const { usage } = usePlan();
  const [photos, setPhotos] = React.useState<ItemPhoto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const maxPerItem = usage?.maxPhotosPerItem ?? 1;
  const storeOriginal = usage?.storeOriginal ?? false;
  const canAddMore = photos.length < maxPerItem;
  const isPro = usage?.plan === "pro";

  const fetchPhotos = React.useCallback(async () => {
    try {
      const data = await apiFetch<ItemPhoto[]>(`/api/items/${itemId}/photos`);
      setPhotos(data);
    } catch {
      // If the endpoint isn't available yet, fall back to empty
      setPhotos([]);
    }
    setLoading(false);
  }, [itemId]);

  React.useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleAddPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so the same file can be re-selected
    e.target.value = "";

    if (!canAddMore) {
      setError(isPro
        ? `Maximum ${maxPerItem} photos per item.`
        : `Your plan allows ${maxPerItem} photo(s) per item. Upgrade to Pro for up to 12.`
      );
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const { displayKey, thumbKey, originalKey } = await uploadPhoto(file, { storeOriginal });

      // Create item_photos row via API
      const newPhoto = await apiFetch<ItemPhoto>(`/api/items/${itemId}/photos`, {
        method: "POST",
        body: JSON.stringify({ displayKey, thumbKey, originalKey }),
      });

      setPhotos((prev) => [...prev, newPhoto]);
      onChanged?.();
    } catch (err: any) {
      if (err?.status === 403) {
        setError(err.message ?? "Photo limit reached. Upgrade to add more photos.");
      } else {
        setError("Photo upload failed. Please try again.");
      }
    }
    setUploading(false);
  };

  const handleDeletePhoto = async (photoId: string) => {
    try {
      await apiFetch(`/api/items/${itemId}/photos/${photoId}`, { method: "DELETE" });
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      onChanged?.();
    } catch {
      setError("Failed to delete photo.");
    }
    setConfirmDeleteId(null);
  };

  const triggerFileInput = () => {
    if (canAddMore) {
      fileInputRef.current?.click();
    } else {
      setError(isPro
        ? `Maximum ${maxPerItem} photos per item.`
        : `Your plan allows ${maxPerItem} photo(s) per item. Upgrade to Pro for up to 12.`
      );
    }
  };

  // If no item_photos rows exist but there's a legacy photoUrl, show it as a single photo
  const showLegacy = photos.length === 0 && legacyPhotoUrl && !loading;

  // Hidden file input for photo selection
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      onChange={handleAddPhoto}
      className="hidden"
    />
  );

  if (loading) {
    return (
      <div className="w-full h-24 rounded-xl bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  // No photos at all - show add prompt
  if (!showLegacy && photos.length === 0 && editable) {
    return (
      <div>
        {fileInput}
        <button
          onClick={triggerFileInput}
          disabled={uploading}
          className="w-full h-24 rounded-xl border-2 border-dashed border-neutral-border/60 flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-gray-400">
              <Camera className="w-4 h-4" />Add photo
            </span>
          )}
        </button>
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {fileInput}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {/* Legacy single photo */}
        {showLegacy && (
          <div className="relative group shrink-0">
            <img
              src={legacyPhotoUrl!}
              alt="Item photo"
              className="w-20 h-20 object-cover rounded-lg"
              loading="lazy"
            />
          </div>
        )}

        {/* Multi-photo gallery */}
        {photos.map((photo) => (
          <div key={photo.id} className="relative group shrink-0">
            <img
              src={photo.thumbUrl || photo.displayUrl || ""}
              alt={`Photo ${photo.sortOrder + 1}`}
              className="w-20 h-20 object-cover rounded-lg"
              loading="lazy"
            />
            {/* HD badge */}
            {photo.hasOriginal && (
              <span className="absolute top-1 right-1 bg-black/60 text-white text-[8px] font-bold px-1 rounded">
                HD
              </span>
            )}
            {/* Delete overlay on hover */}
            {editable && (
              <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                {confirmDeleteId === photo.id ? (
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="px-2 py-1 rounded bg-red-500 text-white text-[10px] font-medium"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="px-2 py-1 rounded bg-white/90 text-[10px] font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(photo.id)}
                    className="p-1.5 rounded-full bg-white/90 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Add more button */}
        {editable && (photos.length > 0 || showLegacy) && (
          <button
            onClick={triggerFileInput}
            disabled={uploading}
            className={cn(
              "w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center shrink-0 transition-colors",
              canAddMore
                ? "border-primary/40 hover:bg-primary/5 cursor-pointer"
                : "border-neutral-border/40 cursor-not-allowed"
            )}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            ) : (
              <div className="flex flex-col items-center">
                <Plus className={cn("w-5 h-5", canAddMore ? "text-primary" : "text-gray-300")} />
                {!canAddMore && !isPro && (
                  <span className="text-[8px] text-gray-400 mt-0.5">Pro</span>
                )}
              </div>
            )}
          </button>
        )}
      </div>

      {/* Photo count & error */}
      <div className="flex items-center justify-between">
        {photos.length > 0 && (
          <p className="text-[10px] text-gray-400">
            {photos.length}/{maxPerItem} photo{maxPerItem !== 1 ? "s" : ""}
            {editable && " \u00b7 Hover to delete"}
          </p>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    </div>
  );
}
